import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useChatStore } from "../../../stores/chatStore";
import { getMessagesByDate } from "../../../axios/chat";
import {
  textFieldStyles,
  popperStyles,
  layoutStyles,
  dayStyles,
  yearButtonStyles,
  canlendarButtonStyles,
} from "./styles";

interface CalendarPanelProps {
  selectedDate: Date | null;
  onDateChange: (date: Date | null) => void;
  onClose: () => void;
}

export const CalendarPanel: React.FC<CalendarPanelProps> = ({
  selectedDate,
  onDateChange,
  onClose,
}) => {
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { setMessages, setTargetMessageId } = useChatStore();

  const minDate = new Date(2023, 0, 1);

  const maxDate = new Date();
  maxDate.setFullYear(new Date().getFullYear() + 1);

  const handleFetchData = async () => {
    if (!selectedDate) return;
    try {
      const isoDate = selectedDate.toISOString();
      const data = await getMessagesByDate(isoDate);
      if (!data.message_id) {
        setErrorMessage("No messages found for the selected date.");
      } else {
        setErrorMessage("");
        setTargetMessageId(data.message_id);
        setMessages(data.pagination);
      }
    } catch (error) {
      console.error("Error fetching messages by date:", error);
      setErrorMessage("An error occurred while fetching messages.");
    } finally {
      onClose();
    }
  };

  return (
    <Box p={2}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          label="Choose date"
          value={selectedDate}
          onChange={(newValue) => {
            setErrorMessage("");
            onDateChange(newValue);
          }}
          minDate={minDate}
          maxDate={maxDate}
          disableFuture
          format="dd.MM.yyyy"
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          slotProps={{
            textField: {
              fullWidth: true,
              onClick: () => setOpen(true),
              sx: textFieldStyles,
            },
            popper: {
              sx: popperStyles,
            },
            layout: {
              sx: layoutStyles,
            },
            day: {
              sx: dayStyles,
            },
            yearButton: {
              sx: yearButtonStyles,
            },
          }}
        />
      </LocalizationProvider>

      {selectedDate && (
        <>
          <Button
            onClick={handleFetchData}
            variant="outlined"
            sx={canlendarButtonStyles}
          >
            Choose date
          </Button>

          {errorMessage && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};
